import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Loader2 } from "lucide-react";
import { FadeUp, Overline } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GROUPS } from "@/lib/products";
import { SITE, waLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  company: "",
  country: "",
  email: "",
  whatsapp_phone: "",
  product_interest: "",
  estimated_quantity: "",
  packaging_requirement: "",
  private_label: "",
  message: "",
};

const FIELD_STYLE =
  "h-12 rounded-none border-0 border-b border-navy/20 bg-transparent px-0 py-2.5 text-base text-navy shadow-none placeholder:text-navy/35 focus-visible:ring-0 focus-visible:border-gold transition-colors duration-300";

const INTEREST_OPTIONS = [...GROUPS.map((g) => g.name), "Private Label Project"];

const buildWhatsAppMessage = (d) => `Hello RBL Exim,
We have received a new B2B product enquiry.

Buyer Details
Name: ${d.name}
Company: ${d.company}
Email: ${d.email}
WhatsApp/Phone: ${d.whatsapp_phone || "—"}
Country: ${d.country}

Product Requirement
Product Category: ${d.product_interest}
Quantity: ${d.estimated_quantity || "—"}
Packaging: ${d.packaging_requirement || "—"}
Private Label Required: ${d.private_label || "No"}
Requirement: ${d.message}

Please follow up with the buyer regarding product availability, MOQ, pricing and export details.
Thank you.`;

export default function QuoteForm() {
  const [form, setForm] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const product = q.get("product");
    const category = q.get("category");
    const pl = q.get("privateLabel");
    const qty = q.get("qty");
    return {
      ...INITIAL,
      product_interest:
        category && INTEREST_OPTIONS.includes(category) ? category : "",
      private_label: pl === "Yes" ? "Yes" : "",
      estimated_quantity: qty || "",
      message: product ? `Product: ${product}\n` : "",
    };
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      const url = waLink(buildWhatsAppMessage(form));
      setWaUrl(url);
      setSubmitted(true);
      toast.success("Enquiry Submitted Successfully", {
        description: "Saved to our trade desk — continue on WhatsApp for the fastest response.",
      });
      window.open(url, "_blank", "noopener,noreferrer");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send your enquiry", {
        description: "Please check the fields and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="bg-white py-20 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
        <FadeUp>
          <Overline>B2B Buyer Enquiry</Overline>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.08] tracking-tight text-navy md:text-5xl">
            Request a Quote
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-navy/70">
            Share your products, quantities and destination. Our team will
            review your requirements and get back to you shortly with
            availability, MOQ and export details.
          </p>

          <div className="mt-10 space-y-6">
            <a href={`mailto:${SITE.email}`} data-testid="enquiry-contact-email" className="group flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-gold">
                <Mail size={19} />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-navy/50">Email</span>
                <span className="mt-0.5 block text-base font-medium text-navy">{SITE.email}</span>
              </span>
            </a>
            <a href={waLink("Hello RBL EXIM, I am interested in your food export products. I would like to discuss product availability, pricing and export requirements.")} target="_blank" rel="noopener noreferrer" data-testid="enquiry-contact-whatsapp" className="group flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-gold">
                <MessageCircle size={19} />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-navy/50">WhatsApp</span>
                <span className="mt-0.5 block text-base font-medium text-navy">{SITE.whatsappDisplay}</span>
              </span>
            </a>
            <div className="flex items-start gap-4" data-testid="enquiry-contact-address">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                <MapPin size={19} />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-navy/50">Address</span>
                <span className="mt-0.5 block text-base font-medium leading-relaxed text-navy">{SITE.address}</span>
              </span>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="rounded-2xl border border-navy/10 bg-cream p-7 md:p-10"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="enquiry-name" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Full Name *</label>
                <Input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2}
                  value={form.name} onChange={set("name")} placeholder="Your full name" className={FIELD_STYLE} />
              </div>
              <div>
                <label htmlFor="enquiry-company" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Company Name *</label>
                <Input id="enquiry-company" data-testid="enquiry-company-input" required minLength={2}
                  value={form.company} onChange={set("company")} placeholder="Your company name" className={FIELD_STYLE} />
              </div>
              <div>
                <label htmlFor="enquiry-country" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Country *</label>
                <Input id="enquiry-country" data-testid="enquiry-country-input" required minLength={2}
                  value={form.country} onChange={set("country")} placeholder="Destination country" className={FIELD_STYLE} />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Email *</label>
                <Input id="enquiry-email" data-testid="enquiry-email-input" type="email" required
                  value={form.email} onChange={set("email")} placeholder="you@company.com" className={FIELD_STYLE} />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">WhatsApp / Phone</label>
                <Input id="enquiry-phone" data-testid="enquiry-phone-input"
                  value={form.whatsapp_phone} onChange={set("whatsapp_phone")} placeholder="With country code" className={FIELD_STYLE} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Product Interested In *</label>
                <Select required value={form.product_interest} onValueChange={set("product_interest")}>
                  <SelectTrigger data-testid="enquiry-interest-select"
                    className="mt-1 h-12 rounded-none border-0 border-b border-navy/20 bg-transparent px-0 text-base shadow-none focus:ring-0 data-[placeholder]:text-navy/35">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent data-testid="enquiry-interest-options" className="rounded-xl border-navy/10">
                    {INTEREST_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`enquiry-interest-${o.split(" ")[0].toLowerCase()}`}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="enquiry-quantity" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Quantity Required</label>
                <Input id="enquiry-quantity" data-testid="enquiry-quantity-input"
                  value={form.estimated_quantity} onChange={set("estimated_quantity")} placeholder="e.g. 1 x 20ft container" className={FIELD_STYLE} />
              </div>
              <div>
                <label htmlFor="enquiry-packaging" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Packaging Requirement</label>
                <Input id="enquiry-packaging" data-testid="enquiry-packaging-input"
                  value={form.packaging_requirement} onChange={set("packaging_requirement")} placeholder="e.g. retail packs in master cartons" className={FIELD_STYLE} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Private Label Required?</label>
                <Select value={form.private_label} onValueChange={set("private_label")}>
                  <SelectTrigger data-testid="enquiry-private-label-select"
                    className="mt-1 h-12 rounded-none border-0 border-b border-navy/20 bg-transparent px-0 text-base shadow-none focus:ring-0 data-[placeholder]:text-navy/35">
                    <SelectValue placeholder="Select Yes or No" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-navy/10">
                    <SelectItem value="Yes" data-testid="enquiry-private-label-yes">Yes</SelectItem>
                    <SelectItem value="No" data-testid="enquiry-private-label-no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="enquiry-message" className="text-xs font-semibold uppercase tracking-[0.16em] text-navy/60">Message *</label>
              <Textarea id="enquiry-message" data-testid="enquiry-message-input" required minLength={10} rows={4}
                value={form.message} onChange={set("message")}
                placeholder="Products, specifications, target price points, labelling needs…"
                className="mt-1 resize-none rounded-none border-0 border-b border-navy/20 bg-transparent px-0 text-base text-navy shadow-none placeholder:text-navy/35 focus-visible:ring-0 focus-visible:border-gold transition-colors duration-300" />
            </div>

            {submitted ? (
              <div data-testid="enquiry-success" className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                <p className="font-display text-xl font-semibold tracking-tight text-navy">
                  Enquiry Submitted Successfully
                </p>
                <p className="mt-2 text-sm leading-relaxed text-navy/70">
                  Thank you for your enquiry. Our team will get back to you
                  shortly.
                </p>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="continue-whatsapp-button"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors duration-300 hover:bg-[#1eb857]"
                >
                  Continue to WhatsApp →
                </a>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="enquiry-send-another"
                  className="mt-4 block w-full text-center text-xs font-medium text-navy/50 transition-colors duration-300 hover:text-navy"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-navy py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
              >
                {sending && <Loader2 size={16} className="animate-spin" />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            )}
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
